<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import { getBeltTextColorHex } from '@@/bits/belt_colors'
import { computed } from 'vue'

interface Props {
  color: BeltColor
  size: number
  strokeSize: number
  progress: number
  isDarkMode: boolean
}

const defaultSize = 108
const defaultStrokeSize = 8.4

const props = withDefaults(defineProps<Props>(), {
  color: 'white',
  size: defaultSize,
  strokeSize: defaultStrokeSize,
  progress: 1,
  isDarkMode: false,
})

const actualStrokeSize = computed(() => {
  if (props.strokeSize) {
    return props.strokeSize
  }
  return props.size / 12.857 // 108px/8.4px = 12.857
})
const decorationSize = computed(() => {
  return props.size / 2.25 // 108px/48px = 2.25
})

const bottomOffset = computed(() => {
  return props.size / 108 // 108px/1px = 108
})
const rightOffset = computed(() => {
  return props.size / 6 // 108px/18px = 6
})

const colorHex = computed(() => {
  return getBeltTextColorHex(props.color)
})

const strokeTotalLength = computed(() => Math.PI * (49.2 * 2))

const decorationColorHex = computed(() => {
  if (props.progress < 1) {
    if (props.isDarkMode) {
      return '#4B4B4B'
    }
    return '#E7E9EA'
  }
  return colorHex.value
})
</script>

<template>
  <div class="flex flex-row relative">
    <!-- Belt / Progress Circle -->
    <svg
      :width="props.size"
      :height="props.size"
      viewBox="0 0 108 108"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="rotate-[225deg] -scale-100"
    >
      <!-- Background circle to show progress -->
      <circle
        v-if="props.progress < 1"
        cx="54"
        cy="54"
        r="49.2"
        :stroke="decorationColorHex"
        :stroke-width="actualStrokeSize"
      />
      <!-- Foreground progress circle / belt -->
      <circle
        v-if="props.progress > 0"
        cx="54"
        cy="54"
        r="49.2"
        :stroke="colorHex"
        :stroke-width="actualStrokeSize"
        :stroke-linecap="'round'"
        :stroke-dasharray="`${props.progress * strokeTotalLength}, ${(1 - props.progress) * strokeTotalLength}`"
      />
    </svg>

    <!-- Belt Decoration (belt sash) -->
    <div class="flex flex-row absolute" :style="{ bottom: `-${bottomOffset}px`, right: `-${rightOffset}px` }">
      <svg :width="decorationSize" :height="decorationSize" viewBox="0 0 48 48" fill="none">
        <path
          d="M21.8052 10.5442C21.8052 10.5442 36.3789 14.051 43.5768 18.3716L30.7527 25.5784C30.7527 25.5784 26.3388 19.0755 20.0279 17.6082L28.3474 40.6478L15.309 40.574L15.0368 19.2942C15.0368 19.2942 17.9375 10.9528 21.8052 10.5442Z"
          :fill="decorationColorHex"
        />
        <path
          d="M15.09 28.9912C18.4391 25.1148 20.466 22.6918 21.2528 20.9329C21.2603 20.8998 21.2702 20.8767 21.2832 20.8638C21.2733 20.8867 21.2631 20.9098 21.2528 20.9329C21.0774 21.7059 22.2038 27.9329 15.5104 37.5913L15.09 28.9912Z"
          fill="black"
          fill-opacity="0.2"
        />
        <path
          d="M11.8333 20.8628C10.7142 19.4718 13.6055 10.4415 18.1336 9.66216C31 12 21.5009 15.9973 11.8333 20.8628Z"
          :fill="decorationColorHex"
        />
        <path
          d="M11.8162 19.9535C16.1203 16.6876 21.1089 17.8081 22.6833 18.7608L20.5829 22.2626C17.7835 18.7608 12.1306 20.2703 11.8162 19.9535Z"
          fill="black"
          fill-opacity="0.2"
        />
      </svg>
    </div>
    <!-- Belt Inner item, comes with color style -->
    <!-- goodcheck-disable-next-line -->
    <div class="flex flex-row absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" :style="{ color: colorHex }">
      <slot></slot>
    </div>
  </div>
</template>
